// Mock dataset lifted from the Claude Design project ("Caption Control.dc.html") so Storybook and tests
// look like the design, expressed in the real protocol shapes. Brand strings are placeholders.
import type {
  AlertRow,
  AuditEntry,
  Device,
  DisplaySettings,
  Me,
  Member,
  Recording,
  Release,
  SecurityStatus,
  Settings,
} from '@glyph/protocol-ts';
import { MASKED_SECRET } from '@glyph/protocol-ts';

export const DEMO_WORDS =
  "Good morning everyone and welcome back to day two of the summit. Before we, um, get into the program I want to thank the crew at FDG who have been here since five this morning. Today we are going to look at where the platform is headed over the next twelve months and what that means for every team in this room. Let's start with the numbers from the last quarter, because they are, uh, frankly better than we expected.".split(
    /\s+/,
  );

export const CURRENT_VERSION = '2.4.1';

const NOW = Date.now() / 1000;
const ago = (s: number) => NOW - s;
const at = (iso: string) => Date.parse(iso) / 1000;

export const DEMO_DEVICES: Device[] = [
  {
    id: 'cc-01',
    serial: '10000000A1B2C3D4',
    name: 'FDG CC-01',
    status: 'active',
    state: 'live',
    online: true,
    last_seen: ago(1),
    agent_version: '2.4.1',
    os_version: '1.2.0',
    slot: 'A',
    org_id: 'org-fdg',
    assignment: { org_name: 'Summit Events', event_id: 'ev-keynote', event_name: 'Keynote — Hall B' },
    asr_model: 'nova-3',
    dg_rtt_ms: 380, cloud_rtt_ms: 38,
    audio_dbfs: -18,
    uptime_s: 14 * 3600 + 22 * 60,
    address: '10.0.4.21',
    uplink: 'eth',
    hotspot_ssid: 'Glyph-CC-01-C3D4',
    enrolled_at: at('2026-10-02T09:00:00Z'),
    key_id: 'k1-7f3a',
    locked: false,
    update_locked: false,
    update_state: null,
    update_target: null,
    update_pct: null,
    update_error: null,
    update_finished_at: null,
  },
  {
    id: 'cc-02',
    serial: '10000000B2C3D4E5',
    name: 'FDG CC-02',
    status: 'active',
    state: 'live',
    online: true,
    last_seen: ago(2),
    agent_version: '2.4.1',
    os_version: '1.2.0',
    slot: 'B',
    org_id: 'org-fdg',
    assignment: { org_name: 'Summit Events', event_id: 'ev-breakout', event_name: 'Breakout — Room 204' },
    asr_model: 'nova-3',
    dg_rtt_ms: 410, cloud_rtt_ms: 38,
    audio_dbfs: -24,
    uptime_s: 14 * 3600 + 20 * 60,
    address: '10.0.4.37',
    uplink: 'wifi',
    hotspot_ssid: 'Glyph-CC-02-D4E5',
    enrolled_at: at('2026-10-02T09:10:00Z'),
    key_id: 'k1-91c2',
    locked: false,
    update_locked: false,
    update_state: null,
    update_target: null,
    update_pct: null,
    update_error: null,
    update_finished_at: null,
  },
  {
    id: 'cc-03',
    serial: '10000000C3D4E5F6',
    name: 'FDG CC-03',
    status: 'active',
    state: 'standby',
    online: true,
    last_seen: ago(3),
    agent_version: '2.4.0',
    os_version: '1.2.0',
    slot: 'A',
    org_id: 'org-fdg',
    assignment: null,
    asr_model: 'idle',
    dg_rtt_ms: null, cloud_rtt_ms: null,
    audio_dbfs: null,
    uptime_s: 2 * 86400 + 3 * 3600,
    address: '10.0.4.52',
    uplink: 'eth',
    hotspot_ssid: 'Glyph-CC-03-E5F6',
    enrolled_at: at('2026-09-20T12:00:00Z'),
    key_id: 'k1-2be0',
    locked: false,
    update_locked: false,
    update_state: null,
    update_target: null,
    update_pct: null,
    update_error: null,
    update_finished_at: null,
  },
  {
    id: 'cc-04',
    serial: '10000000D4E5F6A7',
    name: 'FDG CC-04',
    status: 'active',
    state: 'offline',
    online: false,
    last_seen: at('2026-10-17T13:48:02Z'),
    agent_version: '2.3.8',
    os_version: '1.2.0',
    slot: 'A',
    org_id: 'org-fdg',
    assignment: { org_name: 'City Hall', event_id: 'ev-townhall', event_name: 'Town Hall — Studio' },
    asr_model: '—',
    dg_rtt_ms: null, cloud_rtt_ms: null,
    audio_dbfs: null,
    uptime_s: 0,
    address: null,
    uplink: 'none',
    hotspot_ssid: null,
    enrolled_at: at('2026-08-11T10:00:00Z'),
    key_id: 'k1-aa10',
    locked: false,
    update_locked: false,
    update_state: null,
    update_target: null,
    update_pct: null,
    update_error: null,
    update_finished_at: null,
  },
  {
    id: 'cc-05',
    serial: '10000000E5F6A7B8',
    name: 'FDG CC-05',
    status: 'active',
    state: 'standby',
    online: true,
    last_seen: ago(4),
    agent_version: '2.4.1',
    os_version: '1.2.0',
    slot: 'B',
    org_id: 'org-fdg',
    assignment: null,
    asr_model: 'idle',
    dg_rtt_ms: null, cloud_rtt_ms: null,
    audio_dbfs: null,
    uptime_s: 6 * 86400 + 11 * 3600,
    address: '10.0.4.60',
    uplink: 'eth',
    hotspot_ssid: 'Glyph-CC-05-A7B8',
    enrolled_at: at('2026-09-28T15:00:00Z'),
    key_id: 'k1-03fe',
    locked: false,
    update_locked: true,
    update_state: null,
    update_target: null,
    update_pct: null,
    update_error: null,
    update_finished_at: null,
  },
];

const rec = (
  p: Partial<Recording> &
    Pick<
      Recording,
      | 'id'
      | 'device_id'
      | 'name'
      | 'event_name'
      | 'folder'
      | 'start_ts'
      | 'end_ts'
      | 'size'
      | 'words'
      | 'tc_start'
      | 'framerate'
      | 'upload_status'
    >,
): Recording => ({
  display_name: p.name,
  events: Math.round(p.words / 9),
  sha256: null,
  drive: 'internal',
  has_audio: true,
  tags: [],
  protected: false,
  ...p,
});

export const DEMO_RECORDINGS: Recording[] = [
  rec({
    id: 'Keynote_HallB_Day2',
    device_id: 'cc-01',
    name: 'Keynote_HallB_Day2',
    event_name: 'Keynote — Hall B',
    folder: 'keynote',
    start_ts: at('2026-10-16T09:02:00Z'),
    end_ts: at('2026-10-16T09:02:00Z') + 6138,
    size: 1153434,
    words: 14820,
    tc_start: '09:02:00;00',
    framerate: 29.97,
    upload_status: 'synced',
  }),
  rec({
    id: 'Keynote_HallB_Day1',
    device_id: 'cc-01',
    name: 'Keynote_HallB_Day1',
    event_name: 'Keynote — Hall B',
    folder: 'keynote',
    start_ts: at('2026-10-15T09:00:00Z'),
    end_ts: at('2026-10-15T09:00:00Z') + 5884,
    size: 1048576,
    words: 14102,
    tc_start: '09:00:00;00',
    framerate: 29.97,
    upload_status: 'synced',
  }),
  rec({
    id: 'Breakout_204_Session3',
    device_id: 'cc-02',
    name: 'Breakout_204_Session3',
    event_name: 'Breakout — Room 204',
    folder: 'breakout',
    start_ts: at('2026-10-15T14:00:00Z'),
    end_ts: at('2026-10-15T14:00:00Z') + 3160,
    size: 593920,
    words: 7730,
    tc_start: '14:00:00;00',
    framerate: 29.97,
    upload_status: 'synced',
  }),
  rec({
    id: 'Breakout_204_Session2',
    device_id: 'cc-02',
    name: 'Breakout_204_Session2',
    event_name: 'Breakout — Room 204',
    folder: 'breakout',
    start_ts: at('2026-10-15T11:30:00Z'),
    end_ts: at('2026-10-15T11:30:00Z') + 2892,
    size: 534528,
    words: 7014,
    tc_start: '11:30:00;00',
    framerate: 29.97,
    upload_status: 'local_only',
  }),
  rec({
    id: 'TownHall_Studio_Rehearsal',
    device_id: 'cc-04',
    name: 'TownHall_Studio_Rehearsal',
    event_name: 'Town Hall — Studio',
    folder: 'townhall',
    start_ts: at('2026-10-12T16:30:00Z'),
    end_ts: at('2026-10-12T16:30:00Z') + 1272,
    size: 219136,
    words: 2904,
    tc_start: '16:30:00:00',
    framerate: 25,
    upload_status: 'synced',
  }),
  rec({
    id: 'Soundcheck_HallB',
    device_id: 'cc-01',
    name: 'Soundcheck_HallB',
    event_name: 'Keynote — Hall B',
    folder: 'keynote',
    start_ts: at('2026-10-14T18:10:00Z'),
    end_ts: at('2026-10-14T18:10:00Z') + 391,
    size: 47104,
    words: 610,
    tc_start: '18:10:00;00',
    framerate: 29.97,
    upload_status: 'synced',
  }),
];

export const DEMO_ALERTS: AlertRow[] = [
  {
    id: 'a1',
    device_id: 'cc-02',
    key: 'deepgram_error',
    state: 'resolved',
    severity: 'warn',
    detail: 'ASR round trip above 800 ms for 12 s — recovered',
    ts: at('2026-10-17T14:02:11Z'),
    count: 1,
    acked_at: null,
    acked_by: null,
  },
  {
    id: 'a2',
    device_id: 'cc-04',
    key: 'offline',
    state: 'firing',
    severity: 'crit',
    detail: 'Heartbeat lost · last seen 13:48:02 · unit was STANDBY',
    ts: at('2026-10-17T13:48:30Z'),
    count: 1,
    acked_at: null,
    acked_by: null,
  },
  {
    id: 'a3',
    device_id: 'cc-01',
    key: 'hdmi_changed',
    state: 'resolved',
    severity: 'info',
    detail: 'HDMI-B connected · LG 27UL · mirroring HDMI-A at 1080p',
    ts: at('2026-10-17T12:15:04Z'),
    count: 1,
    acked_at: at('2026-10-17T12:20:00Z'),
    acked_by: 'matt@fdgtv.com',
  },
  {
    id: 'a4',
    device_id: 'cc-02',
    key: 'audio_silent',
    state: 'resolved',
    severity: 'warn',
    detail: 'Audio input silent for 30 s before session start',
    ts: at('2026-10-17T09:30:00Z'),
    count: 1,
    acked_at: at('2026-10-17T09:32:00Z'),
    acked_by: 'hallb@fdgtv.com',
  },
  {
    id: 'a5',
    device_id: 'cc-01',
    key: 'session_opened',
    state: 'resolved',
    severity: 'info',
    detail: 'Session opened · Deepgram nova-3 · 24 keyterms loaded',
    ts: at('2026-10-17T08:58:40Z'),
    count: 1,
    acked_at: at('2026-10-17T09:00:00Z'),
    acked_by: 'matt@fdgtv.com',
  },
];

export const DEMO_RELEASES: Release[] = [
  {
    id: 'rel-os-130',
    version: '1.3.0-unlocked',
    kind: 'os',
    channel: 'stable',
    published_at: at('2026-10-12T00:00:00Z'),
    size_bytes: 612 * 1048576,
    notes: 'OS image: grows the data volume on any boot it is short · 12 s cap when the wifi radio is missing',
    signed: true,
    min_agent_version: '2.4.0',
    requires_reboot: true,
  },
  {
    id: 'rel-242rc1',
    version: '2.4.2-rc1',
    kind: 'app',
    channel: 'beta',
    published_at: at('2026-10-14T00:00:00Z'),
    size_bytes: 84 * 1048576,
    notes: 'Keyterm batching for long event libraries · HDMI hot-plug under load · OLED shows ASR round trip',
    signed: true,
    min_agent_version: '2.3.0',
    requires_reboot: false,
  },
  {
    id: 'rel-241',
    version: '2.4.1',
    kind: 'app',
    channel: 'stable',
    published_at: at('2026-10-02T00:00:00Z'),
    size_bytes: 83 * 1048576,
    notes: 'Watchdog hardening · A/B rollback verified · hotspot channel selection',
    signed: true,
    min_agent_version: '2.3.0',
    requires_reboot: false,
  },
  {
    id: 'rel-240',
    version: '2.4.0',
    kind: 'app',
    channel: 'stable',
    published_at: at('2026-09-18T00:00:00Z'),
    size_bytes: 83 * 1048576,
    notes: 'Headless DRM/KMS renderer · Nova-3 · cloud relay v2',
    signed: true,
    min_agent_version: '2.2.0',
    requires_reboot: true,
  },
];

export const DEMO_MEMBERS: Member[] = [
  {
    id: 'u1',
    name: 'Matt Marchand',
    email: 'matt@fdgtv.com',
    role: 'owner',
    devices: 'all',
    mfa: true,
    last_seen: NOW,
    status: 'active',
  },
  {
    id: 'u2',
    name: 'Hall B operator',
    email: 'hallb@fdgtv.com',
    role: 'operator',
    devices: ['cc-01', 'cc-02'],
    mfa: true,
    last_seen: ago(120),
    status: 'active',
  },
  {
    id: 'u3',
    name: 'Client producer',
    email: 'producer@summit.events',
    role: 'viewer',
    devices: ['cc-01'],
    mfa: false,
    last_seen: ago(86400),
    status: 'active',
  },
];

export const DEMO_ME: Me = {
  id: 'u1',
  name: 'Matt Marchand',
  email: 'matt@fdgtv.com',
  role: 'owner',
  org: { id: 'org-fdg', name: 'FDGTV', kind: 'platform' },
  mfa_enrolled: true,
};

export const DEMO_AUDIT: AuditEntry[] = [
  {
    id: 'l1',
    ts: ago(60),
    actor: 'matt@fdgtv.com',
    action: 'settings.patch',
    target: 'cc-01',
    detail: 'display',
  },
  {
    id: 'l2',
    ts: ago(600),
    actor: 'hallb@fdgtv.com',
    action: 'audio.listen.start',
    target: 'cc-02',
    detail: '4 min',
  },
  {
    id: 'l3',
    ts: ago(3600),
    actor: 'matt@fdgtv.com',
    action: 'update.offer',
    target: 'cc-03',
    detail: '2.4.1 · skip_if_engine_running',
  },
];

export const DEMO_SECURITY: SecurityStatus = {
  secure_boot: true,
  luks: true,
  clone_check: 'passed',
  attestation_failures_30d: 0,
  identity: { algorithm: 'Ed25519', key_id: 'k1-7f3a', enrolled_at: at('2026-10-02T09:00:00Z') },
  local_ui: { pin_required: true, rate_limited: true, bound_to: ['10.42.0.1', 'glyph-c3d4.local'] },
  tamper_sensor: 'not_fitted',
  locked: false,
  serial: '10000000A1B2C3D4',
};

/** Design preset "Broadcast default" in the renderer schema (pydantic defaults where the design is silent). */
export const DISPLAY_DEFAULT: DisplaySettings = {
  font_family: 'Helvetica Neue',
  font_file: '',
  font_size_px: 56,
  font_weight: 600,
  font_style: 'normal',
  letter_spacing_px: 0,
  line_height: 1.3,
  text_transform: 'none',
  text_color: '#FFFFFF',
  text_align: 'center',
  max_lines: 2,
  width_pct: 80,
  v_align: 'bottom',
  h_align: 'center',
  offset_x_pct: 0,
  offset_y_pct: 0,
  safe_area_pct: 3.75, // 72 px of 1920
  outline: { enabled: true, color: '#000000', width: 3 },
  text_shadow: { enabled: true, color: '#000000', blur: 8, offset_x: 0, offset_y: 2, opacity: 60 },
  box: {
    enabled: true,
    per_line: false,
    color: '#000000',
    opacity: 80,
    padding_x: 16,
    padding_y: 10,
    radius: 8,
  },
  box_shadow: { enabled: false, color: '#000000', blur: 24, opacity: 60, offset_x: 0, offset_y: 6 },
  mode: 'rollup',
  scroll_duration_ms: 250,
  word_reveal_ms: 45,
  auto_clear_ms: 6000,
  popon_show_interim: false,
  partial_style: { italic: true, opacity: 85 },
  key_color: '#00B140',
  top_fade_px: 8,
};

export const DISPLAY_PRESETS: Record<string, DisplaySettings> = {
  'Broadcast default': DISPLAY_DEFAULT,
  'Stage LED wall': {
    ...DISPLAY_DEFAULT,
    font_family: 'Atkinson Hyperlegible',
    font_weight: 700,
    font_size_px: 84,
    width_pct: 70,
    offset_y_pct: -2.08,
    safe_area_pct: 5,
    auto_clear_ms: 8000,
    text_color: '#FFE14D',
    key_color: '#000000',
    box: { ...DISPLAY_DEFAULT.box, enabled: false },
    text_transform: 'upper',
    letter_spacing_px: 2,
  },
  'Stream lower third': {
    ...DISPLAY_DEFAULT,
    font_family: 'IBM Plex Sans',
    font_weight: 500,
    font_size_px: 40,
    max_lines: 3,
    width_pct: 85,
    mode: 'painton',
    scroll_duration_ms: 210,
    auto_clear_ms: 5000,
    h_align: 'left',
    text_align: 'left',
    safe_area_pct: 5,
    box: { ...DISPLAY_DEFAULT.box, per_line: true, opacity: 90 },
    outline: { ...DISPLAY_DEFAULT.outline, enabled: false },
    text_shadow: { ...DISPLAY_DEFAULT.text_shadow, enabled: false },
  },
};

export const SETTINGS_DEFAULT: Settings = {
  config_version: 1,
  deepgram: {
    model: 'nova-3',
    language: 'en-US',
    endpointing: 300,
    utterance_end_ms: 1000,
    interim_results: true,
    punctuate: true,
    smart_format: true,
    numerals: true,
    filler_words: false,
    profanity_filter: true,
    redact: [],
    diarize: false,
    vad_events: true,
  },
  audio: {
    device: 'Scarlett 2i2 · USB',
    sample_rate: 48000,
    channels: 2,
    program_channel: 1,
    ltc_channel: 2,
    silence_dbfs: -55,
    silence_alert: false,
    silence_alert_s: 120,
    ring_seconds: 10,
  },
  display: DISPLAY_DEFAULT,
  outputs: {
    policy: 'mirror',
    assignments: { 'HDMI-A': 'main', 'HDMI-B': 'main' },
    mode_override: '',
    keep_signal_on_loss: true,
    auto_detect: true,
  },
  network: {
    ap_enabled: true,
    ap_ssid_suffix: 'auto',
    ap_password: MASKED_SECRET,
    ap_open: false,
    ap_hidden: false,
    ap_band: '5',
    ap_channel: 36,
    ap_ip: '10.42.0.1',
    wifi_enabled: true,
    wifi_band: 'auto',
    eth_method: 'auto',
    eth_address: '',
    eth_gateway: '',
    eth_dns: '',
    hostname: '',
    lte_enabled: false,
    lte_apn: '',
  },
  device: { name: 'CC-01' },
  auth: { admin_password: MASKED_SECRET, tech_username: 'tech', tech_password: '', pin: '', api_token: '' },
  system: { oled_enabled: true, oled_show_ap_pass: false, oled_cloud_mode: 'auto' },
  record: {
    tc_source: 'system',
    tc_host: '',
    tc_framerate: 29.97,
    tc_start: '00:00:00:00',
    tc_offset_frames: 0,
    record_audio: true,
    audio_format: 'flac',
    always_on: true,
    save_dir: '',
    drive_uuid: '',
    drive_subpath: 'glyph',
    drive_fallback_to_internal: true,
    retention_days: 0,
    min_free_gb: 2,
    store_raw: false,
    export_chars_per_line: 42,
    export_zero: 'session',
    export_zero_tc: '01:00:00:00',
  },
  wordlib: { active_library_id: 'wl-keynote', blocked_mode: 'drop', vulgarity_filter: true },
  cloud: {
    enabled: true,
    auto_enrol: true,
    url: 'wss://relay.glyph.example/gw/device',
    relay_captions: true,
    heartbeat_s: 5,
    upload_policy: 'metadata_only',
  },
  partner: { enabled: false, role: 'primary', follow: 'off', peer_host: '' },
  update: { channel: 'stable', lock: false },
  translation: { enabled: false },
  setup: { completed: true },
};

export const DEMO_WORD_LIBRARY = {
  library_id: 'wl-keynote',
  version: '7',
  name: 'Keynote — Hall B',
  event_id: 'ev-keynote',
  keyterms: [
    { term: 'FDG', boost: 2 as const },
    { term: 'Deepgram', boost: 2 as const },
    { term: 'Nova-3', boost: 3 as const },
    { term: 'Hostinger', boost: 1 as const },
    { term: 'Marchand', boost: 3 as const },
    { term: 'chroma key', boost: 1 as const },
  ],
  replacements: {},
  blocked: ['um', 'uh', 'you know', 'damn', 'crap'],
  blocked_mode: 'drop' as const,
  vulgarity_filter: true,
  vulgarity_custom: [],
};

export const DEMO_EVENTS = ['Keynote — Hall B', 'Breakout — Room 204', 'Town Hall — Studio'];
